import { SpinResponse, Win, InitialResponse, Coordinate, CheckStateResult, Movement } from 'src/shared/util/Types';
import { GameState, GlobalState } from 'src/shared/util/GlobalState';
import { observer } from 'src/shared/util/Observer';

export type CheatData = {
    panel: string[][];
}

let cheatData: CheatData | null = null;

export const CHEAT_DATA = {
    'BigWin': {
        panel: [
            ['1', '1', '110', '2', '3'],
            ['1', '1', '2', '2', '3'],
            ['1', '1', '2', '2', '3'],
            ['4', '4', '5', '1', '1'],
            ['4', '4', '5', '1', '1'],
            ['4', '4', '5', '1', '1'],
        ],
    },
    'SuperWin': {
        panel: [
            ['1', '1', '130', '2', '3'],
            ['1', '1', '2', '2', '3'],
            ['1', '1', '2', '2', '3'],
            ['4', '4', '5', '1', '1'],
            ['4', '4', '5', '1', '1'],
            ['4', '4', '5', '1', '1'],
        ],
    },
    'MegaWin': {
        panel: [
            ['1', '1', '150', '2', '3'],
            ['1', '1', '2', '2', '3'],
            ['1', '1', '2', '2', '3'],
            ['4', '4', '5', '1', '1'],
            ['4', '4', '5', '1', '1'],
            ['4', '4', '5', '1', '1'],
        ],
    },
    'Bonus': {
        panel: [
            ['1', '1', '2', '2', '3'],
            ['1', '10', '2', '2', '3'],
            ['1', '10', '2', '2', '3'],
            ['4', '4', '5', '5', '10'],
            ['4', '4', '5', '5', '10'],
            ['4', '4', '5', '5', '1'],
        ],
    },
}

export const paytable: Record<string, number[]> = {
    "0": [0.5, 1.0, 2.0],
    "1": [0.5, 1.0, 2.0],
    "2": [0.4, 0.8, 1.6],
    "3": [0.3, 0.6, 1.2],
    "4": [0.2, 0.4, 0.8],
    "5": [0.1, 0.2, 0.4],
    "6": [0.05, 0.1, 0.2],
    "7": [0.05, 0.1, 0.2],
    "8": [0.05, 0.1, 0.2],
    "9": [0.05, 0.1, 0.2],
    "10": [1.0, 2.0, 5.0],
};

type Multiplier = 2 | 3 | 5 | 20 | 50;

const multiplierWeights: Record<Multiplier, number> = {
    2: 10,
    3: 5,
    5: 3,
    20: 1,
    50: 1,
};

let oldBalance: number = 1000;
let bet: number = 1;
let freespins: number = 0;
let totalMultiplier: number = 1;
let totalWin: number = 0;
let roundWin: number = 0;
let state: GameState = GameState.MAIN;
let oldPanel: string[][] = [[]];

observer.subscribe('rigSelected', onCheatSelected.bind(this));

export function initGenerator(): InitialResponse {
    const panel = _generateRandomPanel();
    const panelNew = _generateNewPanel(panel);

    return {
        currencyCode: "eur",
        panel,
        panelNew,
        rigData: CHEAT_DATA,
        state,
        maxWin: 5000,
        bet,
        paytable,
        bets: [0.1, 0.2, 0.5, 1, 1.5, 2, 3, 5, 10, 20, 30, 50, 100],
        balance: oldBalance,
    }
}

export function spinParser(spinData: any): SpinResponse {
    totalWin = 0;
    roundWin = 0;
    const panel: string[][] = cheatData ? JSON.parse(JSON.stringify(cheatData.panel)) : _generateRandomPanel();
    const panelNew: string[][] = _generateNewPanel(panel);
    const multiplier: number = _calculateMultiplier(panelNew);
    const wins: Win[] = _calculateWins(panelNew, multiplier);
    // only include scatter wins if there are no regular wins present on the panel
    const scatterWins: Win[] = wins.length === 0 ? _calculateScatterWins(panelNew, multiplier) : [];
    const movements: Movement[] = _getMovements(panelNew, [...scatterWins, ...wins]);
    const cascadeRoundWin = _parseRoundWin([...scatterWins, ...wins]);
    const foundScatters: Win = _findScatters(panelNew, '10');
    const { state, scatters, freespins } = _checkState(foundScatters, true, [...scatterWins, ...wins]);

    const isBuyBonus: boolean = spinData.BuyBonus || false;
    const betMultiplier = isBuyBonus ? 100 : 1;

    let win: number = 0;

    roundWin += cascadeRoundWin;
    totalWin += cascadeRoundWin;

    // NOTE: During scatter wins, the balance is updated immediately, as there are 
    // no cascades to be parsed, since scatters are non-cascading
    if (wins.length === 0 || (wins.length === 0 && scatterWins.length === 1)) {
        win = totalWin;
    }

    bet = GlobalState.getInstance().bet;
    const balance = oldBalance - (bet * betMultiplier) + win;

    oldBalance = balance;
    cheatData = null;
    return {
        panel,
        movements,
        scatters,
        freespins,
        panelNew,
        state,
        odd: roundWin / bet,
        roundWin,
        balance,
        totalWin,
        multiplier,
        wins,
        scatterWins: scatterWins
    }
}

export function cascadeParser(): SpinResponse {
    const panel: string[][] = oldPanel;
    const cascade: Movement[] = _getCascade(panel);
    const panelNew: string[][] = _generatePostCascadePanel(panel, cascade);
    const multiplier: number = _calculateMultiplier(panelNew);
    const wins: Win[] = _calculateWins(panelNew, multiplier);
    // only include scatter wins if there are no regular wins present on the panel
    const scatterWins: Win[] = wins.length === 0 ? _calculateScatterWins(panelNew, multiplier) : [];
    const movements: Movement[] = _getMovements(panelNew, [...scatterWins, ...wins]);
    const cascadeRoundWin = _parseRoundWin([...scatterWins, ...wins]);
    const foundScatters: Win = _findScatters(panelNew, '10');
    const { state, scatters, freespins } = _checkState(foundScatters, true, [...scatterWins, ...wins]);

    let win: number = 0;

    // Cascade round win will always be at least one if scatters are present
    // as scatters are non-cleared symbols. Scatter wins remain on the panel until
    // the bonus round is started

    if (cascadeRoundWin > 0 || scatterWins.length > 0) {
        roundWin += cascadeRoundWin;
    }

    if (cascadeRoundWin === 0 || (cascadeRoundWin > 0 && scatterWins.length > 0)) {
        roundWin *= multiplier;
        totalWin = roundWin;

        win = totalWin;
    }

    console.log("CASCADE PARSER WIN " + win);
    console.log("CASCADE PARSER ROUND WIN " + roundWin);
    console.log("CASCADE ROUND WIN " + cascadeRoundWin);
    const balance = oldBalance + win;

    oldBalance = balance;
    cheatData = null;
    return {
        panel,
        scatters,
        movements,
        cascade,
        freespins,
        panelNew,
        state,
        odd: roundWin / bet,
        roundWin,
        balance,
        totalWin,
        multiplier,
        wins,
        scatterWins: scatterWins
    }
}

export function bonusSpinParser(): SpinResponse {
    roundWin = 0;
    const panel: string[][] = cheatData ? JSON.parse(JSON.stringify(cheatData.panel)) : _generateRandomPanel();
    const panelNew: string[][] = _generateNewPanel(panel);
    const multiplier: number = _calculateMultiplier(panelNew);
    const wins: Win[] = _calculateWins(panelNew, multiplier);
    const scatterWins: Win[] = _calculateScatterWins(panelNew, multiplier);
    const movements: Movement[] = _getMovements(panelNew, [...scatterWins, ...wins]);
    const cascadeRoundWin = _parseRoundWin([...scatterWins, ...wins]);
    const foundScatters: Win = _findScatters(panelNew, '10');
    const { state, scatters, freespins } = _checkState(foundScatters, true, [...scatterWins, ...wins]);

    if (state === GameState.BONUS && freespins === 4) {
        totalWin = 0;
    }

    let winnings: number = 0;
    if (state === GameState.BONUS_TO_MAIN || freespins === 0) {
        winnings = totalWin;
    }

    roundWin += cascadeRoundWin;
    const balance = oldBalance + winnings;

    oldBalance = balance;
    cheatData = null;
    return {
        panel,
        movements,
        scatters,
        freespins,
        panelNew,
        state,
        odd: roundWin / bet,
        roundWin,
        balance,
        totalWin,
        multiplier: totalMultiplier,
        wins,
        scatterWins: scatterWins
    }
}

export function bonusCascadeParser(): SpinResponse {
    const panel: string[][] = oldPanel;
    const cascade: Movement[] = _getCascade(panel);
    const panelNew: string[][] = _generatePostCascadePanel(panel, cascade);
    const multiplier: number = _calculateMultiplier(panelNew);
    const wins: Win[] = _calculateWins(panelNew, multiplier);
    const scatterWins: Win[] = _calculateScatterWins(panelNew, multiplier);
    const movements: Movement[] = _getMovements(panelNew, [...scatterWins, ...wins]);
    const cascadeRoundWin = _parseRoundWin([...scatterWins, ...wins]);
    const foundScatters: Win = _findScatters(panelNew, '10');
    const { state, scatters, freespins } = _checkState(foundScatters, false, [...scatterWins, ...wins]);

    if (cascadeRoundWin === 0) {
        roundWin *= multiplier;
        totalWin += roundWin;
        totalWin = parseFloat(totalWin.toFixed(2));

        totalMultiplier += multiplier;
    } else {
        roundWin += cascadeRoundWin;
    }

    let winnings: number = 0;
    if (state === GameState.BONUS_TO_MAIN || freespins === 0) {
        winnings = totalWin;
    }

    const balance = oldBalance + winnings;

    oldBalance = balance;
    cheatData = null;
    return {
        panel,
        scatters,
        movements,
        cascade,
        freespins,
        panelNew,
        state,
        odd: roundWin / bet,
        roundWin,
        balance,
        totalWin,
        multiplier: totalMultiplier,
        wins,
        scatterWins: scatterWins
    }
}

function _checkState(scatters: Win, decrement: boolean = true, wins: Win[]): CheckStateResult {
    const regularWins = wins.filter(w => w.symbol !== '10');

    switch (state) {
        case GameState.MAIN:
        case GameState.BONUS_TO_MAIN:
            if (state === GameState.BONUS_TO_MAIN) {
                totalMultiplier = 1; // Reset multiplier when bonus ends
            }
            if (scatters.coordinates.length >= 3 && regularWins.length === 0) {
                state = GameState.MAIN_TO_BONUS;
                freespins = 5;
            } else {
                state = GameState.MAIN;
                scatters.coordinates.length = 0;
            }

            return { state, scatters, freespins };

        case GameState.MAIN_TO_BONUS:
            state = GameState.BONUS;
            freespins--;

            return { state, freespins, scatters };

        case GameState.BONUS:
            if (decrement) {
                freespins--;
            }
            if (freespins === 0 && regularWins.length === 0) {
                state = GameState.BONUS_TO_MAIN;
            }
            return { state, scatters, freespins };
    }
}

function _generateRandomPanel(): string[][] {
    // Generate a 6x5 panel by randomly selecting symbols from the weighted pool
    const panel: string[][] = [];
    for (let col = 0; col < 6; col++) {
        const panelRow: string[] = [];
        for (let row = 0; row < 5; row++) {
            panelRow.push(_getRandomSymbol());
        }
        panel.push(panelRow);
    }

    return panel;
}

function _calculateMultiplier(panel: string[][]): number {
    let multiplier = 0;
    console.log(`_calculateMultiplier called: totalMultiplier = ${totalMultiplier}`);

    for (let row = 0; row < panel.length; row++) {
        for (let col = 0; col < panel[row].length; col++) {
            const symbol = panel[row][col];
            if (parseInt(symbol) > 100) {
                const symbolMultiplier = parseInt(symbol) - 100;
                multiplier += symbolMultiplier;
            }
        }
    }

    // If there are multiplier symbols but no base multiplier, use 1 as base
    if (multiplier > 0) {
        const finalMultiplier = multiplier * totalMultiplier;
        console.log(`Final calc: ${multiplier} * ${totalMultiplier} = ${finalMultiplier}`);
        return finalMultiplier;
    }

    console.log(`No panel multipliers, returning: 1 * ${totalMultiplier} = ${1 * totalMultiplier}`);
    // No multipliers found, return 1 (no effect)
    return 1 * totalMultiplier;
}

function _calculateWins(panel: string[][], multiplier: number): Win[] {
    const symbolCounts: Record<string, number> = {};

    // Count occurrences of each symbol
    for (let reel = 0; reel < panel.length; reel++) {
        for (let row = 0; row < panel[reel].length; row++) {
            const symbol = panel[reel][row];
            if (parseInt(symbol) > 100) {
                continue; // Skip multiplier symbols
            } // Skip multiplier symbols
            symbolCounts[symbol] = (symbolCounts[symbol] || 0) + 1;
        }
    }

    const wins: Win[] = [];

    for (const [symbol, count] of Object.entries(symbolCounts)) {
        if (symbol === '10') {
            continue; // Skip the scatter symbol itself 
        }
        if (count >= 8) {
            let payout = 0;
            if (count >= 8 && count <= 9) {
                payout = paytable[symbol][0];
            } else if (count >= 10 && count <= 11) {
                payout = paytable[symbol][1];
            } else if (count >= 12) {
                payout = paytable[symbol][2];
            }

            if (payout > 0) {
                const coordinates: Coordinate[] = [];
                for (let reel = 0; reel < panel.length; reel++) {
                    for (let row = 0; row < panel[reel].length; row++) {
                        if (panel[reel][row] === symbol) {
                            coordinates.push({ reel, row });
                        }
                    }
                }

                wins.push({
                    symbol,
                    win: payout * bet,
                    multiplier,
                    odd: payout,
                    coordinates,
                });
            }
        }
    }

    return wins;
}

function _calculateScatterWins(panel: string[][], multiplier: number): Win[] {
    const scatterCount: number = _findScatters(panel, '10').coordinates.length;
    const wins: Win[] = [];

    if (scatterCount >= 4) {
        let payout = 0;

        if (scatterCount == 4) {
            payout = paytable["10"][0];
        } else if (scatterCount == 5) {
            payout = paytable["10"][1];
        } else if (scatterCount >= 6) {
            payout = paytable["10"][2];
        }

        if (payout > 0) {
            const coordinates: Coordinate[] = [];
            for (let reel = 0; reel < panel.length; reel++) {
                for (let row = 0; row < panel[reel].length; row++) {
                    if (panel[reel][row] === '10') {
                        coordinates.push({ reel, row });
                    }
                }
            }

            wins.push({
                symbol: '10',
                win: payout * bet,
                multiplier,
                odd: payout,
                coordinates,
            });
        }
    }

    return wins;
}

function _findScatters(panel: string[][], targetSymbol: string): Win {
    const coordinates: Coordinate[] = [];

    for (let reel = 0; reel < panel.length; reel++) {
        for (let row = 0; row < panel[reel].length; row++) {
            if (panel[reel][row] === targetSymbol) {
                coordinates.push({ reel, row });
            }
        }
    }

    return {
        symbol: targetSymbol,
        win: 0,
        odd: 0,
        coordinates
    };
}

function _parseRoundWin(wins: Win[]): number {
    const roundWin = parseFloat(wins.reduce((total, win) => total + win.win, 0).toFixed(2));
    return roundWin;
}

function _generateNewPanel(panel: string[][]): string[][] {
    const newPanel: string[][] = JSON.parse(JSON.stringify(panel));
    return newPanel;
}

function _getRandomSymbol(): string {
    const symbols = ["1", "2", "3", "4", "5", "6", "7", "8", "10", "100"];
    const weights = [25, 24, 30, 30, 50, 50, 50, 50, 3, 5]; // Weights for symbols

    // Create a weighted pool of symbols based on their weights
    const weightedSymbols: string[] = [];
    symbols.forEach((symbol, index) => {
        for (let i = 0; i < weights[index]; i++) {
            weightedSymbols.push(symbol);
        }
    });

    const randomIndex = Math.floor(Math.random() * weightedSymbols.length);
    let symbol: string = weightedSymbols[randomIndex];
    if (symbol === '100') {
        symbol = (100 + _getRandomMultiplier()).toString();
    }
    return symbol;
}

function _getRandomMultiplier(): Multiplier {
    const totalWeight = Object.values(multiplierWeights).reduce((sum, weight) => sum + weight, 0);
    const randomValue = Math.floor(Math.random() * totalWeight);

    let cumulativeWeight = 0;
    for (const [multiplier, weight] of Object.entries(multiplierWeights)) {
        cumulativeWeight += weight;
        if (randomValue < cumulativeWeight) {
            return Number(multiplier) as Multiplier;
        }
    }

    return 2; // Default case, should not happen
}

function _sortAfterCascade(panel: string[][]): Movement[] {
    const movements: Movement[] = [];
    for (let i = 0; i < panel.length; i++) {
        // Step 1: Pair each element with its original index
        const indexedArray = panel[i].map((value, index) => ({ value, index }));

        // Step 2: Sort the array to move nulls to the left, maintaining stability for identical elements
        const sortedArray = [...indexedArray].sort((a, b) => (a.value === 'empty' ? -1 : b.value === 'empty' ? 1 : 0));

        // Step 3: Build the result array and track movements
        const result: string[] = [];

        sortedArray.forEach((item, newIndex) => {
            result[newIndex] = item.value; // Populate the result array
            if (item.index !== newIndex && item.value !== 'empty') {
                movements.push({ symbol: item.value.toString(), from: { reel: i, row: item.index }, to: { reel: i, row: newIndex } });
            }
        });
    }

    // Sort the movements by the 'from.row' value (ascending order)
    movements.sort((a, b) => b.from.row - a.from.row);

    movements.forEach((movement) => {
        const { from, to } = movement;
        panel[from.reel][from.row] = 'empty'; // Set the original position to null
        panel[to.reel][to.row] = movement.symbol; // Move the symbol to the new position
    });
    oldPanel = panel;
    return movements;
}

function _getMovements(panelData: string[][], wins: Win[]) {
    // Only mark non-scatter symbols as empty during main state
    const winSymbols: string[] = wins
        .filter((win: Win) => state === GameState.BONUS || win.symbol !== '10') // Exclude scatters
        .map((win: Win) => win.symbol);

    const panel = JSON.parse(JSON.stringify(panelData));
    for (let i = 0; i < panel.length; i++) {
        for (let j = 0; j < panel[i].length; j++) {
            if (winSymbols.includes(panel[i][j])) {
                panel[i][j] = 'empty'; // Set the original position to null
            }
        }
    }

    return _sortAfterCascade(panel);
}

function _getCascade(panelData: string[][]): Movement[] {
    const panel = JSON.parse(JSON.stringify(panelData));

    const movements: Movement[] = [];
    for (let i = 0; i < panel.length; i++) {
        for (let j = 0; j < panel[i].length; j++) {
            if (panel[i][j] === 'empty') {
                movements.push({ symbol: _getRandomSymbol(), from: { reel: i, row: -1 }, to: { reel: i, row: j } })
            }
        }
    }

    return movements;
}

function _generatePostCascadePanel(panelData: string[][], cascades: Movement[]): string[][] {
    const panel = JSON.parse(JSON.stringify(panelData));

    cascades.forEach((cascade) => {
        const { from, to } = cascade;
        if (from.row === -1) {
            panel[to.reel][to.row] = cascade.symbol;
        } else {
        }
    });

    return panel;
}

function onCheatSelected(cheatCode: string): void {
    if (cheatCode && CHEAT_DATA.hasOwnProperty(cheatCode)) {
        cheatData = JSON.parse(JSON.stringify(CHEAT_DATA[cheatCode as keyof typeof CHEAT_DATA]));
    }
}
